(() => {
  'use strict';
  const page = document.querySelector('.partners-page');
  if (!page) return;

  const formatNumber = (value) => String(value).replace(/\B(?=(\d{3})+(?!\d))/gu, ' ');
  const readInteger = (raw) => {
    if (!raw || /[^0-9]/u.test(raw)) return null;
    const value = Number(raw);
    return Number.isSafeInteger(value) ? value : null;
  };

  const calculator = page.querySelector('#partners-calculator');
  if (calculator) {
    const range = calculator.querySelector('#partners-apartments');
    const apartmentCount = calculator.querySelector('#partners-apartment-count');
    const estimate = calculator.querySelector('#partners-estimate');
    const impressions = readInteger(calculator.dataset.impressions);
    const offers = readInteger(calculator.dataset.offers);
    const minimum = range && readInteger(range.min);
    const maximum = range && readInteger(range.max);
    const step = range && readInteger(range.step);
    if (range && apartmentCount && estimate && impressions !== null && offers > 0
      && minimum > 0 && maximum >= minimum && step > 0) {
      const updateEstimate = () => {
        const apartments = readInteger(range.value);
        if (apartments === null || apartments < minimum || apartments > maximum
          || (apartments - minimum) % step !== 0) return;
        apartmentCount.textContent = formatNumber(apartments);
        estimate.textContent = formatNumber(Math.round(apartments * impressions / offers));
      };
      range.disabled = false;
      range.addEventListener('input', updateEstimate);
      updateEstimate();
    }
  }

  const motion = typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  // Same deferred format selection as the home hero, scoped to this page's Moscow video.
  const initHeroVideo = () => {
    const video = page.querySelector('[data-hero-video]');
    const toggle = page.querySelector('[data-partners-video-toggle]');
    if (!video || !toggle) return;
    const mobile = typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 767px)') : null;
    let scheduled = false;
    let userPaused = false;
    let generation = 0;
    const blocked = () => (mobile ? mobile.matches : window.innerWidth <= 767)
      || (motion && motion.matches);
    const showState = (paused) => {
      toggle.setAttribute('aria-pressed', String(paused));
      toggle.textContent = paused ? 'Продолжить фон' : 'Приостановить фон';
    };
    const restorePoster = () => {
      generation++;
      toggle.hidden = true;
      if (video.hasAttribute('src')) {
        video.pause();
        video.removeAttribute('src');
        video.load();
      }
    };
    const localSource = (raw, extension) => {
      if (!raw) return null;
      try {
        const url = new URL(raw, window.location.href);
        return url.origin === window.location.origin
          && url.pathname === `/video/hero-moscow.${extension}` ? url.pathname + url.search : null;
      } catch { return null; }
    };
    // Relative local video paths pass the shared media gate without becoming image thumbnails.
    const standSrc = (source) => window.standSrc(source);
    const pickFormat = () => {
      try {
        const webm = localSource(video.dataset.videoWebm, 'webm');
        if (webm && video.canPlayType('video/webm; codecs="vp9"')) return webm;
        const mp4 = localSource(video.dataset.videoMp4, 'mp4');
        return mp4 && video.canPlayType('video/mp4') ? mp4 : null;
      } catch { return null; }
    };
    const play = () => {
      const current = ++generation;
      try {
        Promise.resolve(video.play()).then(() => {
          if (current !== generation || blocked()) return;
          userPaused = false;
          showState(false);
          toggle.hidden = false;
        }).catch(() => { if (current === generation) restorePoster(); });
      } catch { restorePoster(); }
    };
    const start = () => {
      if (blocked() || userPaused || video.hasAttribute('src')) return;
      const source = pickFormat();
      if (!source || typeof window.standSrc !== 'function') return;
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('src', standSrc(source));
      video.load();
      play();
    };
    const schedule = () => {
      if (scheduled || blocked() || userPaused || video.hasAttribute('src')) return;
      scheduled = true;
      window.setTimeout(() => {
        const kick = () => { scheduled = false; start(); };
        if (blocked()) { scheduled = false; return; }
        if (typeof window.requestIdleCallback === 'function') {
          window.requestIdleCallback(kick, { timeout: 2500 });
        } else kick();
      }, 1200);
    };
    const mediaChanged = () => { if (blocked()) restorePoster(); else schedule(); };
    [mobile, motion].forEach((media) => {
      if (media && typeof media.addEventListener === 'function') media.addEventListener('change', mediaChanged);
    });
    if (!mobile) window.addEventListener('resize', mediaChanged, { passive: true });
    toggle.addEventListener('click', () => {
      if (toggle.hidden || blocked()) return;
      if (userPaused) play();
      else {
        generation++;
        userPaused = true;
        video.pause();
        showState(true);
      }
    });
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule, { once: true });
    else schedule();
  };
  initHeroVideo();

  const steps = [...page.querySelectorAll('[data-partners-timeline] [data-partners-step]')];
  if (steps.length) {
    let stepObserver;
    const finishSteps = () => {
      if (stepObserver) stepObserver.disconnect();
      steps.forEach((step) => step.classList.add('is-step-visible'));
    };
    if ((motion && motion.matches) || typeof window.IntersectionObserver !== 'function') finishSteps();
    else {
      stepObserver = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-step-visible');
          stepObserver.unobserve(entry.target);
        });
      }, { threshold: 0.25 });
      steps.forEach((step) => stepObserver.observe(step));
      if (motion && typeof motion.addEventListener === 'function') {
        motion.addEventListener('change', (event) => { if (event.matches) finishSteps(); });
      }
    }
  }

  const sticky = page.querySelector('[data-partners-sticky]');
  const form = page.querySelector('#partnersForm');
  if (sticky && form) {
    const mobile = typeof window.matchMedia === 'function'
      ? window.matchMedia('(max-width: 767px)') : null;
    const formVisible = () => {
      const box = form.getBoundingClientRect();
      return box.bottom > 0 && box.top < window.innerHeight;
    };
    let inForm = formVisible();
    const updateSticky = () => {
      if (typeof window.IntersectionObserver !== 'function') inForm = formVisible();
      sticky.hidden = !(mobile ? mobile.matches : window.innerWidth <= 767)
        || window.scrollY <= 24 || inForm;
    };
    if (typeof window.IntersectionObserver === 'function') {
      const formObserver = new window.IntersectionObserver((entries) => {
        entries.forEach((entry) => { if (entry.target === form) inForm = entry.isIntersecting; });
        updateSticky();
      }, { threshold: 0 });
      formObserver.observe(form);
    }
    window.addEventListener('scroll', updateSticky, { passive: true });
    window.addEventListener('resize', updateSticky, { passive: true });
    if (mobile && typeof mobile.addEventListener === 'function') mobile.addEventListener('change', updateSticky);
    updateSticky();
  }

  const counters = new Map();
  page.querySelectorAll('[data-partners-count]').forEach((element) => {
    const target = readInteger(element.dataset.partnersCount);
    if (target !== null) counters.set(element, { target, started: false, frame: null });
  });
  const charts = [...page.querySelectorAll('[data-partners-chart]')];
  if ((!motion || !motion.matches) && typeof window.IntersectionObserver === 'function'
    && typeof window.requestAnimationFrame === 'function' && typeof window.cancelAnimationFrame === 'function') {
    page.classList.add('is-panel-ready');
    let stopped = false;
    const finishMotion = () => {
      stopped = true;
      page.classList.remove('is-panel-ready');
      observer.disconnect();
      counters.forEach((state, element) => {
        if (state.frame !== null) window.cancelAnimationFrame(state.frame);
        element.textContent = formatNumber(state.target);
        state.frame = null;
      });
      charts.forEach((chart) => {
        chart.classList.remove('is-chart-ready');
        chart.classList.add('is-chart-visible');
      });
    };
    const count = (element, state) => {
      if (state.started) return;
      state.started = true;
      element.textContent = '0';
      const start = window.performance.now();
      const advance = (now) => {
        if (stopped || (motion && motion.matches)) {
          finishMotion();
          return;
        }
        const progress = Math.min(1, Math.max(0, (now - start) / 1000));
        element.textContent = formatNumber(Math.round(state.target * (1 - (1 - progress) ** 3)));
        state.frame = progress < 1 ? window.requestAnimationFrame(advance) : null;
      };
      state.frame = window.requestAnimationFrame(advance);
    };
    const observer = new window.IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting || stopped) return;
        if (motion && motion.matches) {
          finishMotion();
          return;
        }
        const state = counters.get(entry.target);
        if (state) count(entry.target, state);
        else entry.target.classList.add('is-chart-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.18 });
    counters.forEach((_state, element) => observer.observe(element));
    charts.forEach((chart) => {
      chart.classList.add('is-chart-ready');
      observer.observe(chart);
    });
    if (motion && typeof motion.addEventListener === 'function') {
      motion.addEventListener('change', (event) => { if (event.matches) finishMotion(); });
    }
  }

  if (!form) return;
  const status = document.getElementById('partners-form-status');
  const button = form.querySelector('button[type="submit"]');
  let sending = false;
  const captcha = form.querySelector('[data-partners-captcha]');
  const captchaField = form.querySelector('[name="smart_token"]');
  let captchaWidget = null;
  const clearCaptcha = () => { if (captchaField) captchaField.value = ''; };
  if (captcha) {
    // docs/SMARTCAPTCHA-SETUP.md: render callback → smart_token → FormData.
    // Ключ публичный; серверный ключ никогда не передаётся браузеру.
    window.onPartnersCaptchaLoad = () => {
      const api = window.smartCaptcha;
      if (captchaWidget !== null || !api || !captcha.dataset.sitekey || !captchaField) return;
      try {
        captchaWidget = api.render(captcha, {
          sitekey: captcha.dataset.sitekey, hl: 'ru',
          callback: (token) => { captchaField.value = typeof token === 'string' ? token : ''; },
        });
        api.subscribe(captchaWidget, 'token-expired', clearCaptcha);
        api.subscribe(captchaWidget, 'javascript-error', clearCaptcha);
      } catch {
        captchaWidget = null;
        clearCaptcha();
      }
    };
    if (window.smartCaptcha) window.onPartnersCaptchaLoad();
  }

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (sending || !form.reportValidity()) return;
    const consent = form.querySelector('[name="consent_main"]');
    if (!consent || !consent.checked) return;
    if (captcha && (captchaWidget === null || !captchaField || !captchaField.value)) {
      status.classList.remove('success');
      status.classList.add('error');
      status.textContent = 'Подтвердите, что вы не робот. Если проверка не загрузилась, позвоните по телефону рядом.';
      return;
    }
    const body = new FormData(form);
    sending = true;
    button.disabled = true;
    status.classList.remove('error', 'success');
    status.textContent = 'Отправляем…';
    try {
      const response = await fetch('/zastroyshchikam.php', {
        method: 'POST', credentials: 'same-origin',
        headers: { Accept: 'application/json' }, body,
      });
      if (!response.ok) throw new Error('Отказ сервера');
      const result = await response.json();
      if (!result.ok) throw new Error('Заявка не принята');
      status.classList.add('success');
      status.textContent = 'Заявка отправлена. Свяжемся с вами выбранным способом.';
      form.reset();
      if (typeof window.ymGoal === 'function') window.ymGoal('partners_form_sent');
    } catch {
      status.classList.add('error');
      status.textContent = 'Не получилось отправить. Попробуйте ещё раз или позвоните по телефону рядом.';
    } finally {
      // Токен одноразовый: даже 422 после проверки телефона требует нового решения.
      clearCaptcha();
      if (captchaWidget !== null && window.smartCaptcha) {
        try { window.smartCaptcha.reset(captchaWidget); } catch { captchaWidget = null; }
      }
      sending = false;
      button.disabled = false;
    }
  });
})();
